import React, { useState, useEffect } from "react";
import { isAdmin } from "../api/controller/userType";
import { useNavigate, useParams } from "react-router-dom";
import { FaSearch, FaHeart, FaFilter, FaFileAlt, FaTimes, FaDownload, FaPencilAlt, FaArrowLeft, FaTrash } from "react-icons/fa";
import resumeImage from "../assets/resumes/resume.webp";
import { ResumeCardData, isFavorite, deleteResume } from "../api/controller/videoController/resume";
import ResumeDialog from "./DialogBoxes/ResumeDialog";
import { IndustryCardData } from "../api/controller/videoController/industry";
import Logo from "../assets/VRP.png";
import Loader from "../components/Loader";
import { useFlashMessage } from "../components/FlashMessageProvider";

const CATEGORY_OPTIONS = [
  "All crimes against a person",
  "Armed Criminal Action (crimes involving a gun)",
  "Crimes within the last 7 years",
  "Crimes within the last 3 years",
  "Drug-related offenses",
  "Embezzlement",
  "Fraud",
  "Murder (1st degree)",
  "Murder (1st and 2nd degree)",
  "Offense involving children",
  "Sexual offense (All)",
  "Sexual offense (involving children)",
  "Theft",
  "Violent crimes",
];
const CATEGORY_SELECT_ALL = "Select All";

// Resume Modal Component
const ResumeModal = ({ showModal, setShowResumeModal, selectedVideo }) => {
  if (!showModal) return null;

  return (
    <div className="fixed inset-0 flex justify-center items-center z-50 font-['corporate-s'] p-4">
      <div className="absolute inset-0 bg-black opacity-50"></div>
      <div className="relative bg-white rounded-lg p-6 w-full max-w-lg shadow-lg z-10 
                max-h-[90vh] overflow-y-auto">
        <button
          onClick={() => setShowResumeModal(false)}
          className="absolute top-4 right-4 text-gray-500 hover:text-red-500"
        >
          <FaTimes size={20} />
        </button>
        <h2 className="text-xl font-bold text-center mb-6 text-[#003DA5]">
          {selectedVideo.name}'s Resume
        </h2>
        {(() => {
          // try several fields where a resume/pdf URL might live
          const url = selectedVideo?.pdfURL || selectedVideo?.resumeURL || selectedVideo?.resume || selectedVideo?.fileURL || null;
          const renderUrl = url || null;
          const ext = renderUrl ? (renderUrl.split('?')[0].split('.').pop() || '').toLowerCase() : '';

          if (ext === 'pdf') {
            return (
              <div className="w-full h-3/4 mb-4">
                <iframe src={renderUrl} title={`${selectedVideo.name} resume`} className="w-full h-full rounded-lg" />
                <div className="flex justify-center mt-2">
                  <a href={renderUrl} download className="bg-[#003DA5] text-white py-2 px-4 rounded-lg font-semibold flex items-center gap-2"><FaDownload /> Download</a>
                </div>
              </div>
            );
          }

          if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext)) {
            return (
              <div>
               <div className="w-full border-2 border-gray-300 rounded-lg overflow-hidden bg-white">
  <div className="max-h-80 overflow-y-auto p-3">
    <img 
      src={renderUrl} 
      alt={`${selectedVideo.name}'s Resume`} 
      className="max-w-full h-auto mx-auto block"
    />
  </div>
</div>                <div className="flex justify-center">
                  <a href={renderUrl} download className="bg-[#003DA5] text-white py-2 px-4 rounded-lg font-semibold flex items-center gap-2"><FaDownload /> Download</a>
                </div>
              </div>
            );
          }

          // Office documents (doc, docx, ppt, xls, etc.) - use Microsoft's online viewer for public URLs
          if (['doc', 'docx', 'ppt', 'pptx', 'xls', 'xlsx'].includes(ext)) {
            const viewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(renderUrl)}`;
            return (
              <div className="w-full h-3/4 mb-4">
                <iframe src={viewerUrl} title={`${selectedVideo.name} document`} className="w-full h-full rounded-lg" />
                <div className="flex justify-center mt-2">
                  <a href={renderUrl} download className="bg-[#003DA5] text-white py-2 px-4 rounded-lg font-semibold flex items-center gap-2"><FaDownload /> Download</a>
                </div>
              </div>
            );
          }

          // fallback: can't preview (docx or unknown) — show placeholder and provide download if available
          return (
            <div>
              <img src={resumeImage} alt="Resume placeholder" className="w-full h-64 object-contain rounded-lg mb-4" />
              <div className="flex justify-center">
                {renderUrl ? (
                  <a href={renderUrl} download className="bg-[#003DA5] text-white py-2 px-4 rounded-lg font-semibold flex items-center gap-2"><FaDownload /> Download</a>
                ) : (
                  <span className="text-gray-500">No resume file available</span>
                )}
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
};

// Video Card Component
export const VideoCard = ({ video, toggleFavorite, openResumeModal, deleteVideo, onEdit }) => {
  return (
    <div className="bg-white rounded-md shadow border-[0.5px] border-[#003DA5] hover:shadow-lg hover:scale-105 transition-transform duration-300 w-full min-h-[480px] relative flex flex-col">
      <video 
        controls 
        className="h-80 w-full flex-shrink-0 object-contain rounded-t-md"
      >
        <source src={video.videoURL} type="video/mp4" />
        Your browser does not support the video tag.
      </video>

      <div className="p-4 flex-grow flex flex-col justify-between">
        <div>
          <h2 className="text-[20px] text-[#003DA5] font-semibold font-['corporate-s'] mb-3 flex items-center gap-2">
            {video.name}
          </h2>
          <p className="flex items-center text-[#00244D] gap-2 font-semibold text-[12px] font-['corporate-s'] mt-2">
            Skills: <span className="text-gray-500">{video.skills.join(', ')}</span>
          </p>
        </div>
        <div className="flex justify-between items-center mt-4 pt-2 border-t border-gray-200">
          <button
            onClick={() => toggleFavorite(video._id)}
            className={`text-${video.isFavorite ? "red-600" : "gray-500"} hover:text-red-600 transition cursor-pointer`}
            title="Favorite"
          >
            <FaHeart size={15} />
          </button>
          {isAdmin() && (
            <button
              onClick={() => onEdit && onEdit(video)}
              className="text-gray-500 hover:text-blue-600 transition cursor-pointer"
              title="Edit"
            >
              <FaPencilAlt size={15} />
            </button>
          )}
          {isAdmin() && (
            <button
              onClick={() => deleteVideo(video._id)}
              className="text-gray-500 hover:text-red-600 transition cursor-pointer"
              title="Delete"
            >
              <FaTrash size={15} />
            </button>
          )}
          <button
            onClick={() => openResumeModal(video)}
            className="text-gray-500 hover:text-blue-600 transition cursor-pointer"
            title="View Resume"
          >
            <FaFileAlt size={15} />
          </button>
        </div>
      </div>
    </div>
  );
};

// Videos Page
const Videos = () => {
  const { showMessage } = useFlashMessage();
  const { region, industry } = useParams();
  const [videos, setVideos] = useState([]);
  const [videoSearch, setVideoSearch] = useState("");
  const [locationFilter, setLocationFilter] = useState("");
  const [industryFilter, setIndustryFilter] = useState("");
  const [industryData, setIndustryData] = useState([]);
  const [categoryFilters, setCategoryFilters] = useState([]);
  const [categoryMenuOpen, setCategoryMenuOpen] = useState(false);
  const categoryRef = React.useRef(null);
  const [transportFilters, setTransportFilters] = useState([]);
  const [transportMenuOpen, setTransportMenuOpen] = useState(false);
  const transportRef = React.useRef(null);
  const [showResumeModal, setShowResumeModal] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [showResumeDialog, setShowResumeDialog] = useState(false);
  const [selectedResume, setSelectedResume] = useState(null);
  const [isLoadingVideos, setIsLoadingVideos] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchResumes = async () => {
      try {
        setIsLoadingVideos(true);
        const data = await ResumeCardData();
        const industryRes = await IndustryCardData();
        setVideos(data);
        setIndustryData(industryRes || []);
      } catch (error) {
        console.error("Error fetching resumes:", error);
      } finally {
        setIsLoadingVideos(false);
      }
    };
    fetchResumes();

    if (region) setLocationFilter(region);
    if (industry) setIndustryFilter(industry);
  }, [region, industry]);

  const handleVideoSearch = (e) => {
    setVideoSearch(e.target.value);
  };
  const handleLocationFilter = (e) => setLocationFilter(e.target.value);
  const handleIndustryFilter = (e) => setIndustryFilter(e.target.value);
  
  const handleCategoryToggle = (option) => {
    setCategoryFilters((prev) => {
      if (option === CATEGORY_SELECT_ALL) {
        const isAllSelected = prev.length === CATEGORY_OPTIONS.length;
        return isAllSelected ? [] : [...CATEGORY_OPTIONS];
      }
      const next = prev.includes(option) ? prev.filter((v) => v !== option) : [...prev, option];
      if (next.length === CATEGORY_OPTIONS.length) {
        return [...CATEGORY_OPTIONS];
      }
      return next;
    });
  };
  const handleTransportToggle = (option) => {
    setTransportFilters((prev) => (prev.includes(option) ? prev.filter((v) => v !== option) : [...prev, option]));
  };

  React.useEffect(() => {
    const onDocClick = (e) => {
      if (transportRef.current && !transportRef.current.contains(e.target)) {
        setTransportMenuOpen(false);
      }
    };
    if (transportMenuOpen) document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [transportMenuOpen]);

  React.useEffect(() => {
    const onDocClick = (e) => {
      if (categoryRef.current && !categoryRef.current.contains(e.target)) {
        setCategoryMenuOpen(false);
      }
    };
    if (categoryMenuOpen) document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [categoryMenuOpen]);

  const toggleFavorite = async (id) => {
    try {
      const videoToUpdate = videos.find((video) => video._id === id || video.id === id);
      if (!videoToUpdate) return;

      const newFavorite = !videoToUpdate.isFavorite;
      const updateFavorite = {
        _id: videoToUpdate._id ?? videoToUpdate.id,
        isFavorite: newFavorite,
      };

      await isFavorite(updateFavorite);

      setVideos((prev) =>
        prev.map((video) =>
          (video._id === updateFavorite._id || video.id === updateFavorite._id) ? { ...video, isFavorite: newFavorite } : video
        )
      );
    } catch (error) {
      console.error(`Error toggling favorite for video with id ${id}:`, error);
    }
  };

  const deleteVideo = async (id) => {
    try {
      setIsLoadingVideos(true);
      await deleteResume(id);
      setVideos((prev) => prev.filter((video) => !(video._id === id || video.id === id)));
      showMessage('Video deleted successfully.', { type: 'success' });
    } catch (error) {
      console.error(`Error deleting video with id ${id}:`, error);
      showMessage('Failed to delete video. Please try again.', { type: 'error' });
    } finally {
      setIsLoadingVideos(false);
    }
  };

  const openResumeModal = (video) => {
    setSelectedVideo(video);
    setShowResumeModal(true);
  };

  const createResume = async () => {
    try {
      setIsLoadingVideos(true);
      const data = await ResumeCardData();
      setVideos(data);
    } catch (error) {
      console.error("Error loading resumes after create:", error);
    } finally {
      setIsLoadingVideos(false);
    }
  };

  const handleEditResume = (resume) => {
    setSelectedResume(resume);
    setShowResumeDialog(true);
  };

  const updateResume = async (updatedResume) => {
    try {
      setIsLoadingVideos(true);
      const data = await ResumeCardData();
      setVideos(data);
      setShowResumeDialog(false);
      setSelectedResume(null);
    } catch (error) {
      console.error("Error updating resume:", error);
    } finally {
      setIsLoadingVideos(false);
    }
  };

  const filteredVideos = React.useMemo(() => {
    const searchTerm = (videoSearch || "").trim().toLowerCase();

    return videos.filter((vid) => {
      const nameText = typeof vid?.name === "string" ? vid.name.toLowerCase() : "";
      let skillsText = "";
      if (Array.isArray(vid?.skills)) {
        skillsText = vid.skills
          .map((skill) => (skill ? skill.toString().toLowerCase() : ""))
          .join(" ");
      } else if (typeof vid?.skills === "string") {
        skillsText = vid.skills.toLowerCase();
      }

      if (searchTerm && !nameText.includes(searchTerm) && !skillsText.includes(searchTerm)) {
        return false;
      }

      const matchesCategory = categoryFilters.length
        ? (Array.isArray(vid?.category)
            ? categoryFilters.every((filter) => vid.category.includes(filter))
            : typeof vid?.category === "string" && vid.category.includes(",")
              ? categoryFilters.every((filter) =>
                  vid.category
                    .split(",")
                    .map((entry) => entry.trim())
                    .includes(filter)
                )
              : categoryFilters.includes(vid?.category))
        : true;

      if (!matchesCategory) return false;

      const matchesTransport = transportFilters.length
        ? (Array.isArray(vid?.transport)
            ? transportFilters.every((filter) => vid.transport.includes(filter))
            : typeof vid?.transport === "string" && vid.transport.includes(",")
              ? transportFilters.every((filter) =>
                  vid.transport
                    .split(",")
                    .map((entry) => entry.trim())
                    .includes(filter)
                )
              : transportFilters.includes(vid?.transport))
        : true;

      if (!matchesTransport) return false;

      if (locationFilter && vid?.location !== locationFilter) return false;
      if (industryFilter && vid?.industry !== industryFilter) return false;

      return true;
    });
  }, [videoSearch, categoryFilters, transportFilters, locationFilter, industryFilter, videos]);

  const uniqueLocations = [...new Set(videos.map((vid) => vid.location))];
  const uniqueIndustries = [...new Set(videos.map((vid) => vid.industry))];

  const resumeDialogCity = React.useMemo(() => {
    if (selectedResume?.location) return selectedResume.location;
    if (locationFilter) return locationFilter;
    if (region) return region;
    return "";
  }, [selectedResume, locationFilter, region]);

  const resumeDialogIndustries = React.useMemo(() => {
    const source = Array.isArray(industryData) ? industryData : [];
    const normalizedCity = resumeDialogCity ? resumeDialogCity.toLowerCase() : "";
    const filtered = normalizedCity
      ? source.filter((ind) => (ind.location || "").toLowerCase() === normalizedCity)
      : source;

    const toCanonicalId = (entity) => {
      if (!entity) return "";
      const value = entity._id || entity.industryId || entity.id;
      return value ? value.toString() : "";
    };

    const map = new Map();
    filtered.forEach((ind) => {
      if (!ind) return;
      const id = toCanonicalId(ind);
      if (!id) return;
      if (!map.has(id)) {
        const name = ind.name ?? ind.industry ?? "";
        map.set(id, { industryId: id, industry: `${name} at ${ind.location ?? ""}` });
      }
    });

    if (map.size === 0 && selectedResume) {
      const selectedRaw = (() => {
        const value = selectedResume.industry;
        if (!value) return "";
        if (typeof value === "object") {
          return (value._id || value.industryId || value.id || "").toString();
        }
        return value.toString();
      })();

      const existing = source.find((ind) => {
        if (!ind) return false;
        const candidateId = toCanonicalId(ind);
        if (candidateId && candidateId === selectedRaw) return true;
        const label = `${ind.name ?? ind.industry ?? ""} at ${ind.location ?? ""}`;
        return label && label === selectedRaw;
      });

      if (existing) {
        const id = toCanonicalId(existing);
        if (id) {
          const name = existing.name ?? existing.industry ?? "";
          map.set(id, { industryId: id, industry: `${name} at ${existing.location ?? ""}` });
        }
      }
    }

    return Array.from(map.values());
  }, [industryData, resumeDialogCity, selectedResume]);

  return (
    <div className="font-['corporate-s']">
                        <section className="relative">
                        <img
                          src={Logo}
                          alt="Video Resume Platform"
                          className="w-full h-64 sm:h-80 md:h-96 lg:h-[500px] object-cover"
                        />
         <div className="absolute inset-0 flex items-center justify-center p-4">
  </div>
      </section>
      <section className="bg-gray-100 py-6 px-4 sm:px-6 lg:px-8 min-h-screen">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-normal font-['farnham-display'] text-[#003DA5] text-center mb-4 sm:mb-6">
            Candidate Videos
          </h2>
          <div className="flex flex-col sm:flex-row sm:flex-wrap sm:justify-between sm:items-center gap-1 sm:gap-3 md:gap-4 mb-4 sm:mb-6">
            <button
              onClick={() => navigate(-1)}
              className="border-2 border-[#003DA5] text-[#fff] bg-[#003DA5] px-2 py-2 font-['corporate-s'] hover:bg-[#003DA5] hover:text-white transition-colors flex items-center gap-2 w-full sm:w-32 md:w-36 text-sm sm:text-base"
            >
              <FaArrowLeft size={14} />
              Industries
            </button>
            <div className="flex flex-col sm:flex-row sm:flex-wrap justify-end items-center gap-1 sm:gap-2 md:gap-2 min-w-0">
              <div className="relative w-full sm:w-40 md:w-48">
                <input
                  type="text"
                  placeholder="Search Name or Skill"
                  className="border border-[#003DA5] bg-white pl-8 pr-3 py-2 text-[#003DA5] text-sm font-semibold w-full focus:outline-none focus:ring-2 focus:ring-[#003DA5]"
                  value={videoSearch}
                  onChange={handleVideoSearch}
                />
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#003DA5] text-[16px]" />
              </div>

              {!industry && (
                <>
                  <div className="relative w-full sm:w-40 md:w-48">
                    <select
                      value={locationFilter}
                      onChange={handleLocationFilter}
                      className="border border-[#003DA5] bg-white pl-8 pr-3 py-2 text-[#003DA5] text-sm font-semibold w-full appearance-none focus:outline-none focus:ring-2 focus:ring-[#003DA5]"
                    >
                      <option endpoints value="">Locations</option>
                      {uniqueLocations.map((loc) => (
                        <option key={loc} value={loc}>
                          {loc}
                        </option>
                      ))}
                    </select>
                    <FaFilter className="absolute left-2 top-1/2 transform -translate-y-1/2 text-[#003DA5] text-sm" />
                  </div>
                  <div className="relative w-full sm:w-40 md:w-48">
                    <select
                      value={industryFilter}
                      onChange={handleIndustryFilter}
                      className="border border-[#003DA5] bg-white pl-8 pr-3 py-2 text-[#003DA5] text-sm font-semibold w-full appearance-none focus:outline-none focus:ring-2 focus:ring-[#003DA5]"
                    >
                      <option value="">Industries</option>
                      {uniqueIndustries.map((ind) => (
                        <option key={ind} value={ind}>
                          {ind}
                        </option>
                      ))}
                    </select>
                    <FaFilter className="absolute left-2 top-1/2 transform -translate-y-1/2 text-[#003DA5] text-sm" />
                  </div>
                </>
              )}
              <div className="relative w-full sm:w-40 md:w-48" ref={categoryRef}>
                <button
                  type="button"
                  onClick={() => setCategoryMenuOpen((s) => !s)}
                  className="w-full text-left border border-[#003DA5] bg-white pl-8 pr-3 py-2 text-[#003DA5] text-sm font-semibold appearance-none focus:outline-none focus:ring-2 focus:ring-[#003DA5]"
                >
                  Offenses
                </button>
                <FaFilter className="absolute left-2 top-1/2 transform -translate-y-1/2 text-[#003DA5] text-sm" />
                {categoryMenuOpen && (
                  <div className="absolute z-30 mt-2 w-full bg-white border border-gray-200  shadow-lg p-2 max-h-64 overflow-auto">
                    {[
                      CATEGORY_SELECT_ALL,
                      ...CATEGORY_OPTIONS,
                    ].map((option) => {
                      const optionKey = option.toLowerCase().replace(/\s+/g, '-');
                      const isSelectAll = option === CATEGORY_SELECT_ALL;
                      const isChecked = isSelectAll
                        ? categoryFilters.length === CATEGORY_OPTIONS.length
                        : categoryFilters.includes(option);
                      return (
                        <label key={optionKey} className="flex items-center gap-2 p-1">
                          <input
                            type="checkbox"
                            className="h-4 w-4"
                            checked={isChecked}
                            onChange={() => handleCategoryToggle(option)}
                          />
                          <span className="text-sm text-[#003DA5]">{option}</span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
              <div className="relative w-full sm:w-40 md:w-48" ref={transportRef}>
                <button
                  type="button"
                  onClick={() => setTransportMenuOpen((s) => !s)}
                  className="w-full text-left border border-[#003DA5] bg-white pl-8 pr-3 py-2 text-[#003DA5] text-sm font-semibold appearance-none focus:outline-none focus:ring-2 focus:ring-[#003DA5]"
                >
                  Transportation Access
                </button>
                <FaFilter className="absolute left-2 top-1/2 transform -translate-y-1/2 text-[#003DA5] text-sm" />
                {transportMenuOpen && (
                  <div className="absolute z-30 mt-2 w-full bg-white border border-gray-200 rounded-md shadow-lg p-2">
                    <label className="flex items-center gap-2 p-1">
                      <input type="checkbox" className="h-4 w-4" checked={transportFilters.includes('Public')} onChange={() => handleTransportToggle('Public')} />
                      <span className="text-sm text-[#003DA5]">Public Transit Accessible</span>
                    </label>
                    <label className="flex items-center gap-2 p-1">
                      <input type="checkbox" className="h-4 w-4" checked={transportFilters.includes('Private')} onChange={() => handleTransportToggle('Private')} />
                      <span className="text-sm text-[#003DA5]">Requires Personal Vehicle</span>
                    </label>
                  </div>
                )}
              </div>
              {isAdmin() && (
                <button
                  onClick={() => {
                    setSelectedResume(null);
                    setShowResumeDialog(true);
                  }}
                  className="bg-[#00244D] text-white px-4 py-2 rounded-lg shadow-lg hover:bg-[#003DA5] focus:outline-none focus:ring-2 focus:ring-[#00244D] focus:ring-offset-2 w-full sm:w-20 md:w-20 lg:w-36 text-sm font-semibold"
                  title="Add Video"
                >
                  Add Video
                </button>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 mt-6 py-12 justify-items-center">
            {isLoadingVideos ? (
              <div className="col-span-full flex items-center justify-center py-16">
                <Loader label="Loading candidates..." size={56} className="flex-col gap-5 text-[#003DA5]" />
              </div>
            ) : filteredVideos.length > 0 ? (
              filteredVideos.map((video, index) => (
                <VideoCard
                  key={video._id || video.id || `video-${index}`}
                  video={video}
                  toggleFavorite={toggleFavorite}
                  openResumeModal={openResumeModal}
                  deleteVideo={deleteVideo}
                  onEdit={() => handleEditResume(video)}
                />
              ))
            ) : (
              <p className="text-center text-gray-500 col-span-full text-sm sm:text-base">No candidates found.</p>
            )}
          </div>
        </div>
      </section>
      <ResumeModal
        showModal={showResumeModal}
        setShowResumeModal={setShowResumeModal}
        selectedVideo={selectedVideo}
      />
      <ResumeDialog
        showModal={showResumeDialog}
        setShowModal={(value) => {
          setShowResumeDialog(value);
          if (!value) {
            setSelectedResume(null);
          }
        }}
        onCreateResume={createResume}
        onEditResume={updateResume}
        selectedResume={selectedResume}
        industries={resumeDialogIndustries}
      />
    </div>
  );
};

export default Videos;